import { useCallback } from "react";
import { useLocation, useNavigate } from "react-router";

import { useBackNavigationContext } from "@/components/contexts/backNavigationContext";
import { isInApp } from "@/screens/utils/hybridAppHelpers";

import { getParentPath, isRootPath } from "./parentPath";

const NO_DISMISSERS = [];
// A second back within this window on the same entry is a double tap, not a new press.
const DOUBLE_BACK_MS = 1000;

// React Router stores the entry's position in the session history; 0 = first page.
const historyIndex = () => globalThis.history?.state?.idx ?? 0;

// history.go(-1) is async, so remember which location the last back left from.
let lastBack = { location: null, at: 0 };

/** Shared back behavior: close a menu (hardware only) → previous page → parent page. */
export function useBackNavigation() {
  const navigate = useNavigate();
  const location = useLocation();
  const { pathname } = location;
  const dismissers = useBackNavigationContext()?.dismissers ?? NO_DISMISSERS;
  const isRoot = isRootPath(pathname);

  const goBack = useCallback(
    ({ hardware = false } = {}) => {
      const dismisser = hardware ? dismissers.at(-1) : null;
      if (dismisser) {
        dismisser.run();
        return;
      }
      const isRepeat =
        lastBack.location === location &&
        Date.now() - lastBack.at < DOUBLE_BACK_MS;
      if (isRepeat) {
        return;
      }
      if (historyIndex() > 0) {
        lastBack = { location, at: Date.now() };
        navigate(-1);
        return;
      }
      const parent = getParentPath(pathname);
      if (parent) {
        lastBack = { location, at: Date.now() };
        navigate(parent, { replace: true });
      }
    },
    [dismissers, location, navigate, pathname],
  );

  return {
    goBack,
    // Whether Android's hardware back stays in the app (false = exit).
    canGoBack: dismissers.length > 0 || !isRoot,
    // Browsers have their own back button; only the app needs one in the page.
    showBack: isInApp() && !isRoot,
    // Named only when back falls through to the parent; a history pop could land anywhere.
    targetLabel:
      historyIndex() === 0 && getParentPath(pathname) === "/browse"
        ? "Articles"
        : null,
  };
}
